import React, { useEffect, FunctionComponent, useState } from 'react'
import { useFetch, notify } from '../../../../components/component-items/helpers'

// Components
import CardLoading from '../../../../components/component-items/loading-popover'
import GlobalStyles from '../../../../components/component-items/styles'
import { StatusBoxStyled } from '../../../../components/component-items/status-box'
import { Pages } from '../../../../components/component-items/pager'
// @ts-ignore
import { format } from 'sql-formatter'
import GenericModal from '../../../../components/component-items/modal'

import { FiltersProps, SortProps } from './index'
import {
  DirectivePreviewInventoryTable,
  DirectivePreviewOrderSection,
  DirectivePreviewSection,
  DirectivePreviewSummaryCards,
} from './wave-directive-preview-sections'

type WavePreviewProps = {
  open: boolean
  setOpen: (open: boolean) => void
  settings: any
  sort: SortProps[]
  filters: FiltersProps
}

type ReservationPreviewSummary = {
  selected: number
  reservable: number
  not_reservable: number
  units_reserved: number
  product_variants: number
  source_mix: { source: string; quantity: number }[]
  inventory_results: any[]
  inventory_results_total?: number
}
const previewPageSize = 10

export const WavePreview: FunctionComponent<WavePreviewProps> = ({ open, setOpen, settings, sort, filters }) => {
  const [showInventoryResults, setShowInventoryResults] = useState(false)
  const [showOrderResults, setShowOrderResults] = useState(true)
  const [showQuery, setShowQuery] = useState(false)
  const [inventoryPage, setInventoryPage] = useState(1)
  const [orderPage, setOrderPage] = useState(1)
  const [previewResponse, setPreviewResponse] = useState<any>(null)
  const { response, loaded, error } = useFetch(open ? '/api/inventory/wave-directive/preview_directive/' : '', {
    method: 'POST',
    body: JSON.stringify({
      ...{ settings, sort, filters },
      preview: true,
      pagination: {
        inventory_page: inventoryPage,
        order_page: orderPage,
        page_size: previewPageSize,
      },
    }),
  })

  useEffect(() => {
    if (loaded && error) {
      notify({ type: 'error', message: error })
    }
    if (loaded && response) {
      setPreviewResponse(response)
    }
  }, [loaded, error, response])

  useEffect(() => {
    if (open) {
      setShowInventoryResults(false)
      setShowOrderResults(true)
      setShowQuery(false)
      setInventoryPage(1)
      setOrderPage(1)
      setPreviewResponse(null)
    }
  }, [open])

  const displayResponse = previewResponse || (loaded ? response : null)
  const hasPreviewResponse = Boolean(displayResponse)
  const refreshingPreview = !loaded && hasPreviewResponse
  const orders = hasPreviewResponse ? displayResponse!['orders'] : []
  const query = hasPreviewResponse ? displayResponse!['query'] : ''

  const filtered = hasPreviewResponse ? displayResponse!['filtered'] : 0
  const total = hasPreviewResponse ? displayResponse!['total'] : 0
  const reservationPreview: ReservationPreviewSummary | null = hasPreviewResponse
    ? displayResponse!['reservation_preview'] || displayResponse!['reservationPreview'] || null
    : null
  const showReservationPreview = Boolean(reservationPreview) || !loaded
  const inventoryResults: any[] =
    reservationPreview?.inventory_results || (reservationPreview as any)?.inventoryResults || []
  const inventoryResultsTotal =
    reservationPreview?.inventory_results_total ||
    (reservationPreview as any)?.inventoryResultsTotal ||
    inventoryResults.length
  const inventoryTotalPages = Math.max(1, Math.ceil(inventoryResultsTotal / previewPageSize))
  const orderTotalPages = Math.max(1, Math.ceil(filtered / previewPageSize))

  useEffect(() => {
    if (loaded && inventoryPage > inventoryTotalPages) {
      setInventoryPage(inventoryTotalPages)
    }
  }, [loaded, inventoryPage, inventoryTotalPages])

  useEffect(() => {
    if (loaded && orderPage > orderTotalPages) {
      setOrderPage(orderTotalPages)
    }
  }, [loaded, orderPage, orderTotalPages])

  let formattedQuery = ''
  try {
    formattedQuery = query ? format(query, { language: 'postgresql' }) : ''
  } catch (error) {
    console.error('SQL formatting error:', error)
  }

  return (
    <GenericModal
      heading={
        <span>
          Batch Preview{' '}
          <small>
            {hasPreviewResponse ? filtered : '-'} out of {hasPreviewResponse ? total : '-'} Total
            {refreshingPreview ? ' · Updating...' : ''}
          </small>
        </span>
      }
      show={open}
      size="xl"
      onHide={() => setOpen(false)}
      buttons={<GlobalStyles.Button onClick={() => setOpen(false)}>Close</GlobalStyles.Button>}
    >
      <div style={{ minHeight: 500, padding: '0 1em 2em' }}>
        {showReservationPreview ? (
          <DirectivePreviewSummaryCards
            metrics={
              reservationPreview
                ? {
                    selected: reservationPreview.selected,
                    reservable: reservationPreview.reservable,
                    not_reservable: reservationPreview.not_reservable,
                    units_reserved: reservationPreview.units_reserved,
                    product_variants: reservationPreview.product_variants,
                  }
                : null
            }
          />
        ) : null}
        {showReservationPreview ? (
          <DirectivePreviewSection
            title="Inventory Results"
            count={hasPreviewResponse ? inventoryResultsTotal : '-'}
            open={showInventoryResults}
            setOpen={setShowInventoryResults}
          >
            <DirectivePreviewInventoryTable rows={hasPreviewResponse ? inventoryResults : []} />
            {!hasPreviewResponse || refreshingPreview ? <CardLoading /> : null}
            {inventoryResultsTotal > previewPageSize ? (
              <GlobalStyles.CardFooter style={{ justifyContent: 'flex-end' }}>
                <Pages page={inventoryPage} setPage={setInventoryPage} total={inventoryTotalPages} show={5} />
              </GlobalStyles.CardFooter>
            ) : null}
          </DirectivePreviewSection>
        ) : null}
        <DirectivePreviewOrderSection
          orders={orders}
          count={filtered}
          open={showOrderResults}
          setOpen={setShowOrderResults}
          page={orderPage}
          setPage={setOrderPage}
          pageSize={previewPageSize}
          loaded={hasPreviewResponse}
          loading={!hasPreviewResponse || refreshingPreview}
        />
        {query ? (
          <DirectivePreviewSection title="SQL Query" open={showQuery} setOpen={setShowQuery}>
            <StatusBoxStyled
              className="silver"
              style={{ margin: 0, whiteSpace: 'pre-line', userSelect: 'all', overflowX: 'auto' }}
            >
              <pre>{formattedQuery}</pre>
            </StatusBoxStyled>
          </DirectivePreviewSection>
        ) : null}
      </div>
    </GenericModal>
  )
}
